import type { PropsWithChildren, ReactNode } from 'react';
import { Children } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconChevronDown } from '@sorare/core/src/atoms/icons/system/iconChevronDown.ts';
import { iconChevronUp } from '@sorare/core/src/atoms/icons/system/iconChevronUp.ts';
import { Collapse as CollapseAtom } from '@sorare/core/src/atoms/layout/Collapse';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { useToggle } from '@sorare/core/src/hooks/useToggle';

const Collapse = styled(CollapseAtom)`
  overflow: hidden;
  padding: 0 var(--section-padding, var(--double-unit));
`;
const LeftAlignedVertical = styled(Vertical)`
  align-items: flex-start;
`;
const SBHorizontalWithPadding = styled(SBHorizontal)`
  padding: 0 var(--section-padding, var(--double-unit));
`;

type Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  expandedByDefault?: boolean;
  textColor?: string;
};
export const Section = ({
  children,
  title,
  subtitle,
  expandedByDefault = true,
  textColor,
}: PropsWithChildren<Props>) => {
  const [expanded, toggleExpanded] = useToggle(expandedByDefault);
  const resolvedTextColor = textColor ?? 'var(--c-white)';

  return (
    <Vertical>
      {!!title && (
        <ButtonBase
          onClick={toggleExpanded}
          style={{ color: resolvedTextColor }}
        >
          <SBHorizontalWithPadding>
            <LeftAlignedVertical gap={0.5}>
              <Horizontal gap={0.5}>
                {title}
                {!expanded && (
                  <BrandXXS>({Children.toArray(children).length})</BrandXXS>
                )}
              </Horizontal>
              <BodyS>{subtitle}</BodyS>
            </LeftAlignedVertical>
            <FontAwesomeIcon
              icon={expanded ? iconChevronUp : iconChevronDown}
            />
          </SBHorizontalWithPadding>
        </ButtonBase>
      )}
      <Collapse open={expanded} gap={0.5}>
        <Vertical gap={1.5}>{children}</Vertical>
      </Collapse>
    </Vertical>
  );
};
